import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "./AppLayout";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ArrowLeft, Clock } from "lucide-react";
import authService from "../Services/authService";

const AttendanceHistoryScreen = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());

  const [month, setMonth] = useState("june");
  const [year, setYear] = useState(currentYear.toString());
  const [summary, setSummary] = useState({});
  const [attendanceList, setAttendanceList] = useState([]);

  const monthMap = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };

  useEffect(() => {
    loadAttendance();
  }, [month, year]);

  const loadAttendance = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + "monthwiseattendance");

    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        month: monthMap[month],
        year: parseInt(year),
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        if (result?.data) {
          const { summary, totalmonth } = result.data;
          setSummary(summary);

          const formattedList = totalmonth.map((entry) => {
            const dateObj = new Date(entry.date);
            const dayName = dateObj.toLocaleDateString("en-US", {
              weekday: "long",
            });
            const dateLabel = dateObj.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });

            const totalHours = entry.total_working_hours
              ? entry.total_working_hours.split(":").map(Number)
              : [0, 0];
            const formattedHours = entry.total_working_hours
              ? `${parseInt(totalHours[0])}h ${parseInt(totalHours[1])}m`
              : "-";

            return {
              date: dateLabel,
              day: dayName,
              clockIn: entry.check_in || "-",
              clockOut: entry.check_out || "-",
              status: entry.status,
              hours: formattedHours,
            };
          });

          setAttendanceList(formattedList);
        } else {
          setAttendanceList([]);
          setSummary({});
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  return (
    <AppLayout>
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Back</span>
        </Button>
        <h1 className="text-xl font-bold text-gray-800">Attendance History</h1>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex space-x-2">
          <Select defaultValue={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(monthMap).map((m) => (
                <SelectItem key={m} value={m}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select defaultValue={year} onValueChange={setYear}>
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="mb-6 border-gray-200">
        <div className="p-4 text-left">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Monthly Summary</h2>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 p-2 rounded-md text-center">
              <div className="text-sm text-gray-500">Working Days</div>
              <div className="text-lg font-semibold text-gray-800">
                {summary?.total_counted_days || 0}
              </div>
            </div>
            <div className="bg-gray-50 p-2 rounded-md text-center">
              <div className="text-sm text-gray-500">Present</div>
              <div className="text-lg font-semibold text-green-600">
                {summary?.present_days || 0}
              </div>
            </div>
            <div className="bg-gray-50 p-2 rounded-md text-center">
              <div className="text-sm text-gray-500">Leave</div>
              <div className="text-lg font-semibold text-amber-600">
                {summary?.leave_days || 0}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="space-y-4 text-left">
        <h2 className="text-lg font-semibold text-gray-800">Daily Records</h2>
        <div className="space-y-3">
          {attendanceList.length > 0 ? (
            attendanceList.map((day, index) => (
              <Card key={index} className="border-gray-200">
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-gray-800">{day.date}</h3>
                      <p className="text-sm text-gray-500">{day.day}</p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        day.status === "Present"
                          ? "bg-green-100 text-green-800"
                          : day.status === "Leave"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {day.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>
                        {day.clockIn} - {day.clockOut}
                      </span>
                    </div>
                    <span>{day.hours}</span>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="border-gray-200">
              <div className="p-4 text-center text-gray-500 text-sm font-medium">
                No Records Found
              </div>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default AttendanceHistoryScreen;
